/* eslint-disable react-hooks/rules-of-hooks */
import { useEffect } from 'react'

import { Box, Wizard, WizardStep } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import { useActionWizard } from 'modules/processes/components/ItemWizard/Actions/ActionProvider'
import { WizardData } from 'modules/processes/components/ItemWizard/Actions/types'
import { useItemProvider } from 'modules/processes/components/ItemWizard/Providers/item'
import { ActionConfigurationIdentifiers } from 'modules/processes/domain'
import { wizardConfiguredActions } from 'modules/processes/lib/wizardConfiguredActions'

import ActionSelectorHeader from './Header'

interface Props {
  data: WizardData
  onDataChanged: (newData: WizardData) => void
  setTitle: (title: string) => void
}

const ActionSelector = ({ data, onDataChanged, setTitle }: Props) => {
  const { actions, loading } = useActionWizard()
  const components = wizardConfiguredActions()

  if (loading)
    return (
      <Box height="s400">
        <Wizard.Loading />
      </Box>
    )

  const { setItemType } = useItemProvider()

  const onSelect = (newActionIdentifier: ActionConfigurationIdentifiers) => {
    const actionFound = actions.find((a) => a.identifier === newActionIdentifier)
    if (!actionFound) return

    onDataChanged({ ...data, action: actionFound })
    setItemType(newActionIdentifier)
  }

  useEffect(() => {
    if (data.action?.identifier) onSelect(data.action?.identifier)

    setTitle(i18n.t('processes.process_settings.actions.config.steps.select'))
    onDataChanged({ ...data })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <ActionSelectorHeader key={`${data.action?.identifier}-header`} />
      <Box gap="s16" paddingY="s24" paddingRight="s16" key={data.action?.identifier}>
        {actions.map((action) => {
          const Component = components[action.identifier]?.selector

          if (!Component) return null

          return (
            <Component
              key={action.identifier}
              identifier={action.identifier}
              title={action.title}
              description={action.description}
              onSelect={onSelect}
            />
          )
        })}
      </Box>
    </>
  )
}

export const actionSelectorStep = (): WizardStep<WizardData> => ({
  title: i18n.t('processes.process_settings.actions.config.steps.select'),
  nextText: i18n.t('common.next'),
  render: (props) => (
    <ActionSelector
      data={props.data}
      onDataChanged={props.onDataChanged}
      setTitle={props.data.setTitle}
    />
  ),
  isCompleted: (data) => !!data.action?.identifier,
})
